import React from "react";
import { ChevronRight } from "lucide-react";

/**
 * Acceptance Pathway diagram — a 5-step horizontal flow showing how
 * support evolves through the deliberative process.
 * Minimal, professional; uses the same muted palette as the rest of
 * the dashboard.
 */
const STEPS = [
  {
    id: 1,
    label: "Technology introduced",
    detail: "Participants presented with onshore wind as an option",
  },
  {
    id: 2,
    label: "High theoretical acceptance",
    detail: "Broad openness to wind in principle",
  },
  {
    id: 3,
    label: "Constraint introduced",
    detail: "SDNP · landscape sensitivity · planning assumptions",
  },
  {
    id: 4,
    label: "Conditional support",
    detail: "Support remains — conditions emerge",
  },
  {
    id: 5,
    label: "Design response",
    detail: "Fewer/larger · clustering · infrastructure-aligned siting",
  },
];

export default function AcceptancePathway() {
  return (
    <section data-testid="acceptance-pathway">
      <div className="mb-4">
        <h2 className="text-xl font-semibold tracking-tight text-gray-900">
          Acceptance pathway
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          How engagement with onshore wind typically evolves across a
          deliberative workshop. Support is not lost at the constraint stage —
          it is re-shaped.
        </p>
      </div>

      <div className="bg-white border border-gray-200 p-5">
        <ol className="flex flex-col lg:flex-row lg:items-stretch gap-3 lg:gap-0">
          {STEPS.map((s, i) => {
            const isLast = i === STEPS.length - 1;
            return (
              <React.Fragment key={s.id}>
                <li
                  className="flex-1 border border-gray-200 bg-[#FAF9F6] p-4 min-w-0"
                  data-testid={`pathway-step-${s.id}`}
                >
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#2F4F4F]">
                    <span className="inline-flex h-5 w-5 items-center justify-center bg-[#2F4F4F] text-white font-mono text-[11px]">
                      {s.id}
                    </span>
                    <span>Step {s.id}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mt-2 leading-snug">
                    {s.label}
                  </p>
                  <p className="text-xs text-gray-600 mt-1 leading-snug">
                    {s.detail}
                  </p>
                </li>
                {!isLast ? (
                  <div
                    className="hidden lg:flex items-center justify-center px-1 text-gray-300"
                    aria-hidden
                  >
                    <ChevronRight className="h-5 w-5" />
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </ol>

        <p className="text-xs text-gray-500 mt-4 max-w-3xl">
          The pathway reinforces the core analytical principle of this
          dashboard: distinguish between rejection of the technology, rejection
          of a specific location, and a perceived planning constraint. These
          are not the same thing.
        </p>
      </div>
    </section>
  );
}
